<script setup lang="ts">
  import { ref, onMounted, provide, watch, onUnmounted, computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import AppHeaderEdu from '@/components/AppHeaderEdu/AppHeaderEdu.vue'
  import shortLinkRewrite from '@/utils/shortLinkRewrite'
  import emitter, { EventBusKey } from '@/utils/eventBusMitt'
  import getDomains from '@/utils/getDomains'
  import { useUserStore } from '@zaohaowu/website-common/stores/userStore'
  import messageQueue from '@/utils/messageQueue'
  import httpApi from '@/http/api'
  import { useConfigStore } from '@/stores/configStore'
  import { LocalStorageKey } from '@/utils/constants'
  import getUserInfo from '@/utils/getUserInfo'
  import http from '@/http'
  import { useTicketStore } from '@zaohaowu/website-common/stores/userTicket'
  import { useFetchDialogData } from '@/hooks/useFetchDialogData'
  import AppPostDialog from '@/components/AppPostDialog/index.vue'

  interface HomeCreateNew {
    curKey: string
    preKey?: string
    newVisible: boolean
  }

  defineOptions({ name: 'EduApp' })

  const ticketStore = useTicketStore()
  const newConfigStore = useConfigStore()
  const userStore = useUserStore()
  const catchComponentNames = [
    'AppFeeds',
    'AppHome',
    'AppChallengeDetail',
    'AppAllActivities',
    'AppAccount',
    'customerAccount',
    'AppOrderList',
    'home'
  ].join(',')
  const cachedComponents = ref(catchComponentNames.split(','))
  const route = useRoute()
  const router = useRouter()
  const headerTransparent = ref(false)
  const showCreateEntry = ref(true)
  const needFooter = ref(true)
  const footerDelay = ref(2000)
  const generatorCacheNames = ref<string[]>([])

  // 是否为教育站点
  const isEdu = getDomains().thirdLevelDomain === 'edu'

  // 说明：在打包后 route.meta 可能为空，是因为 vue-router 的 route 对象在某些情况下不会自动带上 meta，需要从 matched 最后一项获取
  const needHeader = computed(() => {
    // 路由还未准备好时，不显示 header，避免闪烁
    if (!route.matched || route.matched.length === 0) {
      return false
    }
    // 优先从 matched 最后一项的 meta 取，因为 route.meta 在路由切换时可能有延迟
    const matched = route.matched
    const meta = matched[matched.length - 1].meta || {}
    return meta.needHeader === undefined || meta.needHeader === true
  })

  // 清除缓存
  const clearKeepCache = (keys: string[] = []) => {
    if (keys.length > 0) {
      keys.forEach(key => {
        const index = cachedComponents.value.indexOf(key)
        if (index > -1) {
          cachedComponents.value.splice(index, 1)
        }
      })
    } else {
      cachedComponents.value = []
    }
    window.setTimeout(() => {
      cachedComponents.value = catchComponentNames.split(',')
    }, 200)
  }

  provide('clearKeepCache', clearKeepCache)

  // 工具页点击外部隐藏历史记录抽屉
  const hideHistorySlide = (e: MouseEvent) => {
    const historySlideContainer = document.getElementById('history-slide-container')
    const target = e.target as HTMLElement
    if (historySlideContainer && !historySlideContainer.contains(target)) {
      emitter.emit(EventBusKey.showHistorySlide, false)
    }
  }

  // 获取New标识配置
  const getNewTagConfig = async () => {
    const res: any = await httpApi.getNewTagConfig()
    newConfigStore.setNewTagConfig(res.data)
    const homeCreatNew = localStorage.getItem(LocalStorageKey.homeCreatNew)
    // 如果本地没有存，则存入接口返回的
    if (!homeCreatNew) {
      localStorage.setItem(LocalStorageKey.homeCreatNew, JSON.stringify(res.data.homeCreatNew))
    } else {
      // 如果本地有存，则比较curKey，如果curKey不一致，则存入接口返回的
      const homeCreatNewObj: HomeCreateNew = JSON.parse(homeCreatNew)
      if (
        homeCreatNewObj.curKey !== res.data.homeCreatNew.curKey ||
        !res.data.homeCreatNew.newVisible
      ) {
        localStorage.setItem(LocalStorageKey.homeCreatNew, JSON.stringify(res.data.homeCreatNew))
      }
    }
  }

  const getUser = async () => {
    try {
      const res: any = await getUserInfo()
      userStore.setIsLogin(true)
      userStore.setUser(res.data)
      ticketStore.setTicket(res.data.balance || '0')
      if (!isEdu) {
        renewalToken()
      }
    } catch (error) {
      userStore.setIsLogin(false)
      userStore.clearUser()
    }
  }

  // 续约token
  const renewalToken = () => {
    http.get('/aigc/api/auth/renewalToken')
  }

  onMounted(async () => {
    await getUser()

    if (!isEdu) {
      getNewTagConfig()
    }

    shortLinkRewrite()
    if (!isEdu) {
      useFetchDialogData(router)
    }
    router.afterEach((to, from) => {
      // 这部分逻辑只针对工具页缓存
      // 如果to的页面是需要缓存的，把组件name加入缓存数组
      if (to.meta.keepAlive) {
        !generatorCacheNames.value.includes(to.name as string) &&
          generatorCacheNames.value.push(to.name as string)
      }
      // 如果from的页面是需要缓存的，并且to的页面深度小于from的页面深度，则把from的页面从缓存数组中移除
      if (from.meta.keepAlive && (to.meta.depth as number) < (from.meta.depth as number)) {
        if (generatorCacheNames.value.includes(from.name as string)) {
          generatorCacheNames.value = generatorCacheNames.value.filter(name => name !== from.name)
        }
      }

      // 教育版工具页需要增加返回按钮
      if (to.name === 'generator') {
        emitter.emit(EventBusKey.eduHeaderBackBtnVisible, true)
        messageQueue.send(EventBusKey.eduHeaderBackBtnVisible, { visible: true })
      } else {
        emitter.emit(EventBusKey.eduHeaderBackBtnVisible, false)
        messageQueue.send(EventBusKey.eduHeaderBackBtnVisible, { visible: false })
      }

      if (!to.meta.scrollTopClose) {
        window.scrollTo(0, 0)
      }

      // 用于埋点参数的PageName
      localStorage.setItem('currentPageName', to.name as string)
      localStorage.setItem('previousPageName', from.name as string)
      localStorage.setItem('previousPagePath', from.path as string)
    })

    window.addEventListener('click', hideHistorySlide)

    // Rem适配
    setRem()
    window.addEventListener('resize', setRem)

    onUnmounted(() => {
      window.removeEventListener('click', hideHistorySlide)
      window.removeEventListener('resize', setRem)
      window.removeEventListener('load', setRem)
    })
  })

  watch(
    () => route.path,
    () => {
      headerTransparent.value = (route.meta.headerTransparent as boolean | undefined) ?? false
      showCreateEntry.value = (route.meta.showCreateEntry as boolean | undefined) ?? true
      needFooter.value = (route.meta.needFooter as boolean | undefined) ?? true
      footerDelay.value = (route.meta.footerDelay as number | undefined) ?? 2000
    },
    { immediate: true }
  )

  const getDeviceWidth = () => {
    return Math.min(
      document.documentElement.clientWidth || document.body.clientWidth,
      window.innerWidth
    )
  }

  // Rem适配
  const setRem = () => {
    if (route.meta.useSelfRem === true) {
      return
    }
    const designWidth = 1440 //设计稿宽度
    const remBase = 10 // 基准值, 1rem = 10px
    let deviceWidth = getDeviceWidth()
    // 限制最大和最小宽度，防止过大或过小, 320为最小宽度，1920为最大宽度
    deviceWidth = Math.min(Math.max(deviceWidth, 1024), 1920)
    const fontSize = (deviceWidth / designWidth) * remBase
    document.documentElement.style.fontSize = `${fontSize}px`
  }
</script>

<template>
  <AppHeaderEdu v-if="needHeader" :headerTransparent="headerTransparent" />
  <router-view v-slot="{ Component }">
    <keep-alive :include="[...cachedComponents, ...generatorCacheNames]">
      <component :key="$route.fullPath" :is="Component" />
    </keep-alive>
  </router-view>

  <!-- 全局发布弹窗（教育版和主站共用） -->
  <AppPostDialog />
</template>
